import { get, type FieldErrors, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormContext } from './Form';

/**
 * Best-effort i18n: if the validation message looks like a translation key
 * (contains a '.'), translate it; otherwise pass it through.
 *
 * Lets shared Zod schemas keep emitting raw English while letting custom
 * UI-only refines emit i18n keys (e.g. 'auth.passwordsMustMatch').
 */
export function useFieldErrorTranslator(): (message: string | undefined) => string | undefined {
  const { t } = useTranslation();
  return (message) => {
    if (!message) return undefined;
    if (message.includes('.') && !message.includes(' ')) {
      return t(message, { defaultValue: message });
    }
    return message;
  };
}

/** Look up a (possibly nested) RHF error message by dotted/indexed path. */
export function getFieldError<TValues extends FieldValues>(
  errors: FieldErrors<TValues>,
  name: string,
): string | undefined {
  const node = get(errors, name) as { message?: unknown } | undefined;
  if (!node || typeof node !== 'object') return undefined;
  return typeof node.message === 'string' ? node.message : undefined;
}

/** Hook used by field components to get the translated error for `name`. */
export function useFieldError(name: string): string | undefined {
  const {
    formState: { errors },
  } = useFormContext();
  const translate = useFieldErrorTranslator();
  return translate(getFieldError(errors, name));
}
